import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import MathText from '@/components/MathText';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import SEOHead, { seoConfigs } from '@/components/SEOHead';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import FloatingTrialBubble from '@/components/FloatingTrialBubble';

export default function QuickQuiz() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [quizId, setQuizId] = useState(null);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const hasStartedRef = useRef(false);

  useEffect(() => {
    // Use ref to prevent duplicate API calls (especially in React Strict Mode)
    if (!hasStartedRef.current) {
      hasStartedRef.current = true;
      startQuiz();
    }
  }, []);

  const startQuiz = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/quick-quiz/start`);
      setQuestions(response.data.questions);
      setQuizId(response.data.quiz_id);
      setStartTime(new Date().toISOString());
      setLoading(false);
    } catch (error) {
      console.error('Error starting quiz:', error);
      setLoading(false);
    }
  };

  const handleAnswer = (answer) => {
    setAnswers(prev => ({
      ...prev,
      [currentQuestion.id]: answer
    }));
  };

  const goToQuestion = (index) => {
    if (index >= 0 && index < questions.length) {
      setCurrentQuestionIndex(index);
    }
  };

  const handleSubmit = () => {
    // Store results in localStorage and navigate to results
    // User can submit even without answering all questions
    localStorage.setItem('quickQuizResults', JSON.stringify({
      quizId,
      questions,
      answers,
      startTime,
      completedAt: new Date().toISOString()
    }));

    navigate('/quick-quiz/results');
  };

  const handleExit = () => {
    navigate('/');
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', padding: '2rem' }}>
        <SEOHead {...seoConfigs.quickQuiz} />
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>Loading Quiz...</div>
          <div style={{ color: '#666' }}>Preparing your questions</div>
        </div>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <SEOHead {...seoConfigs.quickQuiz} />
        <h2>No questions available</h2>
        <Button onClick={() => navigate('/')}>Return Home</Button>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];

  // Exit button for header
  const exitButton = (
    <Button 
      onClick={() => setShowExitDialog(true)}
      variant="outline"
      style={{ 
        background: 'white',
        color: '#0030b3',
        border: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem'
      }}
    >
      <X size={20} />
      Exit Quiz
    </Button>
  );

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <SEOHead {...seoConfigs.quickQuiz} />
      <Header 
        variant="minimal" 
        onLogoClick={() => setShowExitDialog(true)}
        rightContent={exitButton}
      />
      <FloatingTrialBubble />

      {/* Main Content */}
      <div style={{ flex: 1, background: '#f5f5f5' }}>
        <div style={{ maxWidth: '1200px', margin: '2rem auto', padding: '0 2rem' }}>
          {/* Quiz Title */}
          <h1 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#0030b3', marginBottom: '1rem', textAlign: 'center' }}>
            Short Quiz
          </h1>

          <div style={{ background: 'white', borderRadius: '8px', padding: '2rem', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            {/* Section Labels */}
            <div style={{ marginBottom: '1rem', display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <div style={{ 
                padding: '0.5rem 1rem', 
                borderRadius: '6px', 
                background: currentQuestionIndex < 5 ? '#6366f1' : '#e5e7eb',
                color: currentQuestionIndex < 5 ? 'white' : '#666',
                fontWeight: '600',
                fontSize: '0.85rem'
              }}>
                R&W (1-5)
              </div>
              <div style={{ 
                padding: '0.5rem 1rem', 
                borderRadius: '6px', 
                background: currentQuestionIndex >= 5 ? '#0030b3' : '#e5e7eb',
                color: currentQuestionIndex >= 5 ? 'white' : '#666',
                fontWeight: '600',
                fontSize: '0.85rem'
              }}>
                Math (6-10)
              </div>
            </div>

            {/* Question Navigator */}
            <div style={{ marginBottom: '2rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem', justifyContent: 'center' }}>
              {questions.map((q, idx) => {
                const isRW = idx < 5;
                const sectionColor = isRW ? '#6366f1' : '#0030b3';
                return (
                  <button
                    key={q.id}
                    onClick={() => goToQuestion(idx)}
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '4px',
                      border: `2px solid ${idx === currentQuestionIndex ? sectionColor : '#ddd'}`,
                      background: answers[q.id] ? sectionColor : 'white',
                      color: answers[q.id] ? 'white' : (idx === currentQuestionIndex ? sectionColor : '#666'),
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                  >
                    {idx + 1}
                  </button>
                );
              })}
            </div>
            {/* Question Header */}
            <div style={{ 
              marginBottom: '1.5rem',
              paddingBottom: '1rem',
              borderBottom: '2px solid #f0f0f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1a202c' }}>
                Question {currentQuestion.question_number || currentQuestion.number}
              </div>
              <div style={{ 
                padding: '0.25rem 0.75rem', 
                borderRadius: '4px', 
                background: currentQuestionIndex < 5 ? '#6366f1' : '#0030b3',
                color: 'white',
                fontWeight: '600',
                fontSize: '0.8rem'
              }}>
                {currentQuestionIndex < 5 ? 'Reading & Writing' : 'Math'}
              </div>
            </div>

            {/* Question Text */}
            <div style={{ marginBottom: '1.5rem', fontSize: '1rem', lineHeight: '1.6' }}>
              <MathText>{currentQuestion.question_text}</MathText>
            </div>

            {/* Image if exists */}
            {currentQuestion.image_url && (
              <div style={{ marginBottom: '1.5rem' }}>
                <img 
                  src={currentQuestion.image_url} 
                  alt="Question diagram" 
                  style={{ 
                    maxWidth: 'min(400px, 100%)', 
                    width: 'auto',
                    height: 'auto',
                    borderRadius: '8px',
                    display: 'block'
                  }}
                />
              </div>
            )}

            {/* Answer Input */}
            <div>
              {currentQuestion.question_type === 'student_produced_response' ? (
                // SPR Question - Text Input
                <div>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '0.5rem', 
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: '#1a202c'
                  }}>
                    Your Answer
                  </label>
                  <input
                    type="text"
                    value={answers[currentQuestion.id] || ''}
                    onChange={(e) => handleAnswer(e.target.value)}
                    placeholder="Enter your answer"
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      fontSize: '1rem',
                      border: '2px solid #ddd',
                      borderRadius: '4px',
                      outline: 'none'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#0030b3'}
                    onBlur={(e) => e.target.style.borderColor = '#ddd'}
                  />
                  <p style={{ 
                    marginTop: '0.5rem', 
                    fontSize: '0.875rem', 
                    color: '#666' 
                  }}>
                    Enter a numerical answer (e.g., 5, 3.14, -2.5)
                  </p>
                </div>
              ) : (
                // Multiple Choice - Answer Choices
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {currentQuestion.choices.map((choice) => (
                    <button
                      key={choice}
                      onClick={() => handleAnswer(choice)}
                      style={{
                        padding: '1rem',
                        border: `2px solid ${answers[currentQuestion.id] === choice ? '#0030b3' : '#ddd'}`,
                        background: answers[currentQuestion.id] === choice ? '#e6f0ff' : 'white',
                        borderRadius: '4px',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: '1rem',
                        transition: 'all 0.2s'
                      }}
                    >
                      <strong>{choice}.</strong> <MathText>{currentQuestion.choice_texts[choice]}</MathText>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Navigation */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2rem' }}>
            <Button
              onClick={() => goToQuestion(currentQuestionIndex - 1)}
              disabled={currentQuestionIndex === 0}
              variant="outline"
            >
              <ChevronLeft size={20} />
              Previous
            </Button>
            
            {currentQuestionIndex === questions.length - 1 ? (
              <Button 
                onClick={() => setShowSubmitDialog(true)}
              >
                Submit Quiz
              </Button>
            ) : (
              <Button 
                onClick={() => goToQuestion(currentQuestionIndex + 1)}
              >
                Next
                <ChevronRight size={20} />
              </Button>
            )}
            </div>
          </div>
        </div>
      </div>

      <Footer />

      {/* Submit Dialog */}
      <Dialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Submit Quiz?</DialogTitle>
            <DialogDescription>
              Are you ready to submit your quiz? You can review your answers before submitting.
            </DialogDescription>
          </DialogHeader>
          <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
            <Button variant="outline" onClick={() => setShowSubmitDialog(false)} style={{ flex: 1 }}>
              Review Answers
            </Button>
            <Button onClick={handleSubmit} style={{ flex: 1, background: '#10b981' }}>
              Submit Quiz
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Exit Dialog */}
      <Dialog open={showExitDialog} onOpenChange={setShowExitDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Exit Quiz?</DialogTitle>
            <DialogDescription>
              Are you sure you want to exit? Your progress will not be saved.
            </DialogDescription>
          </DialogHeader>
          <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
            <Button variant="outline" onClick={() => setShowExitDialog(false)} style={{ flex: 1 }}>
              Cancel
            </Button>
            <Button onClick={handleExit} style={{ flex: 1, background: '#d32f2f' }}>
              Exit Quiz
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}